import React from "react";
import { Button, GridColumn, GridRow, ModalDialog, SingleSelect, OverflowText, FlexColumn } from "designSystem/index";
import TextField from "designSystem/components/textfield/TextField";
import { useManageFashionInspirationStore } from "../../ManageFashionInspirationStore";
import { observer } from "mobx-react-lite";
import LoadingPlaceholder from "designSystem/components/placeholder/LoadingPlaceholder";
import { ExplorationCategory } from "../../types";
import { useLocale } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import styles from "./NewExplorationDialog.module.css";

const NewExplorationDialog = observer(() => {
	const loc = useLocale();
	const fashionInspirationStore = useManageFashionInspirationStore();
	const {
		isOnlyOneCategoryInGroup,
		selectedGroup,
		onChangeGroup,
		groups,
		categoriesPerGroup,
		isLoadingNewExploration,
		selectedCategory,
		onChangeCategory,
		onChangeExplorationName,
		explorationName,
		createNewExploration,
		openNewExplorationDialog,
		setCloseNewExplorationDialog,
		setOpenIframeDialog,
	} = fashionInspirationStore;
	const newExplorationDialogTitle = [loc.resource("NewExplorationDialogTitle").toLocaleLowerCase()];

	const handleNewExploration = async () => {
		await createNewExploration(loc.resource("NewExplorationName", [selectedCategory?.name.toLocaleLowerCase()]));
		setCloseNewExplorationDialog();
		setOpenIframeDialog();
	};

	return (
		<ModalDialog
			onClose={setCloseNewExplorationDialog}
			open={openNewExplorationDialog}
			showClose
			titleRes="NewExplorationDialogTitle"
		>
			{{
				buttons: (
					<GridRow justifyContent="flex-end" spacing={2}>
						<Button
							design="link"
							disabled={isLoadingNewExploration}
							onClick={setCloseNewExplorationDialog}
							res="Cancel"
						/>
						<Button
							design="primary"
							disabled={isLoadingNewExploration || !selectedCategory?.id}
							onClick={handleNewExploration}
							res="NewExplorationDialogInspireMeLabel"
						/>
					</GridRow>
				),
				main: (
					<FlexColumn
						gap={4}
						marginBottom={8}
						minWidth={500}
						paddingTop={2}
					>
						{isLoadingNewExploration ? (
							<LoadingPlaceholder
								subtitleProps={{
									res: "LoadingInProgress",
									resParams: newExplorationDialogTitle,
								}}
								titleProps={{ res: "Loading" }}
							/>
						)
							: (
								<FlexColumn gap={4}>
									<GridColumn data-csi-automation="ProductCategory" spacing={1}>
										<OverflowText
											className={styles.asterisk}
											res="NewExplorationCategory"
											semiBold
										/>
										<SingleSelect
											fullWidth
											getOptionLabel={(option: ComponentItem<string>) => option.id}
											onChange={onChangeGroup}
											options={groups}
											required
										/>
									</GridColumn>
									<GridColumn data-csi-automation="ProductType" spacing={1}>
										<OverflowText res="NewExplorationType" semiBold />
										<SingleSelect
											disabled={!selectedGroup || isOnlyOneCategoryInGroup}
											fullWidth
											getOptionLabel={(option: ExplorationCategory) => option.name}
											isOptionEqualToValue={(option, value) => option.id === value.id}
											onChange={onChangeCategory}
											options={categoriesPerGroup}
											required
											value={selectedCategory}
										/>
									</GridColumn>
									<GridColumn data-csi-automation="ExplorationName" spacing={1}>
										<OverflowText res="NewExplorationDialogInputLabel" semiBold />
										<TextField
											fullWidth
											onChange={onChangeExplorationName}
											value={explorationName}
										/>
									</GridColumn>
								</FlexColumn>
							)}
					</FlexColumn>
				),
			}}
		</ModalDialog>
	);
});

export default NewExplorationDialog;
